import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Chip, Typography } from "@mui/material";
import SupervisorApi from "../apis/supervisorApi";
import TicketDetailSection, { TicketSectionPanel } from "./TicketDetailSection";

const api = new SupervisorApi();

export interface TicketTimeEntry {
  id: number;
  technician: string;
  visibility?: "INTERNAL" | "PUBLIC";
  dateWorked?: string;
  startDateTime?: string;
  endDateTime?: string;
  hoursWorked?: number;
  hoursToBill?: number;
  offsetHours?: number;
  summaryNotes?: string;
  internalNotes?: string;
  isNonBillable?: boolean;
  showOnInvoice?: boolean;
  role?: string;
  billingCode?: string;
  contract?: string;
  entryType?: string;
  approvalStatus?: string;
  createdAt?: string;
  updatedAt?: string;
}

const entryTimestamp = (entry: TicketTimeEntry) => {
  const value = entry.createdAt || entry.startDateTime;
  if (value) return new Date(value).toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
  // A work date alone has no time; keep its calendar date without inventing midnight.
  return entry.dateWorked ? new Date(entry.dateWorked.slice(0, 10) + "T00:00:00").toLocaleDateString("en-US", {
    year: "numeric", month: "short", day: "numeric",
  }) : "";
};
const hours = (value: number) => `${Number(value.toFixed(4))} h`;

export default function TicketWorklogSection({ ticketId, identity, refreshKey, resizable = false, onEntriesChange, renderContent }: {
  ticketId: string;
  identity: string;
  refreshKey: unknown;
  resizable?: boolean;
  onEntriesChange?: (entries: TicketTimeEntry[]) => void;
  renderContent?: (entry: TicketTimeEntry) => React.ReactNode;
}) {
  const [result, setResult] = useState<{ key: string; entries: TicketTimeEntry[]; error?: boolean }>();
  const [retry, setRetry] = useState(0);
  const key = `${identity}:${ticketId}`;
  useEffect(() => {
    let active = true;
    api.getTicketTimeEntries(ticketId).then((response: { timeEntries: TicketTimeEntry[] }) => {
      if (active) {
        setResult({ key, entries: response.timeEntries });
        onEntriesChange?.(response.timeEntries);
      }
    }).catch(() => {
      if (active) {
        setResult({ key, entries: [], error: true });
        onEntriesChange?.([]);
      }
    });
    return () => { active = false; };
  }, [ticketId, key, refreshKey, retry, onEntriesChange]);

  if (result?.key !== key) return null;
  if (result.error) {
    const error = (
      <Alert severity="warning" sx={{ mx: 2, flexShrink: 0 }} action={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>}>
        Unable to load time entries.
      </Alert>
    );
    return resizable ? <TicketSectionPanel panelId="worklog" order={3} label="Time Entries" divider>{error}</TicketSectionPanel> : error;
  }
  if (!result.entries.length) return null;
  return (
    <TicketDetailSection
      title="Time Entries"
      label="Time Entries"
      scrollLabel="Time entries"
      panelId={resizable ? "worklog" : undefined}
      order={3}
      divider
    >
      {result.entries.map(entry => (
          <Box key={entry.id} component="article" sx={{ p: 1.5, mb: 1.5, border: "1px solid #EEEAF7", borderRadius: "8px", background: "#FAF9FC", overflowWrap: "anywhere" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center", mb: 1 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14, color: "#172033" }}>{entry.technician}</Typography>
              <Typography variant="caption" color="text.secondary">{entryTimestamp(entry)}</Typography>
              {entry.visibility && <Chip size="small" variant="outlined" label={entry.visibility === "INTERNAL" ? "Internal" : "Public"} />}
              {entry.hoursWorked != null && <Chip size="small" label={`${hours(entry.hoursWorked)} worked`} />}
              {entry.isNonBillable != null && <Chip size="small" variant="outlined" label={entry.isNonBillable ? "Non-billable" : "Billable"} />}
            </Box>
            {renderContent?.(entry) ?? <>
            {entry.summaryNotes && <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 14, color: "#4F5D70" }}>{entry.summaryNotes}</Typography>}
            {entry.internalNotes && <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Internal notes</Typography>
              <Typography sx={{ whiteSpace: "pre-wrap", fontSize: 14, color: "#4F5D70" }}>{entry.internalNotes}</Typography>
            </Box>}
            </>}
          </Box>
      ))}
    </TicketDetailSection>
  );
}
